/**
 * 金额计算
 * 普通模式，根据前端选择的单商品、多商品优惠，优惠券，积分，进位规则计算相关费用
 * 加菜模式，附加费，优惠金额，总计均有后端返回，前端仅做绑定
 */

import couponCalculator from './coupon-priv-calculator';
import {
  getDishBenefitPrice,
  countIntegralsToCash,
  countTotalPriceWithoutBenefit,
  getMaxIntegralCanBeUsedForYaZuo,
  carryPrice,
  getOrderExtraPrice
} from '../../helper/order-helper';
import { getDishesPrice } from '../../helper/dish-helper';
import { formatPrice } from '../../helper/common-helper';
import { calcServiceCharge, calcDishForTaxType } from './service-charge-calc';
import { getFinalDishesWidthPurchase } from './change-purchase-calculator';
import { TAX_MODE } from '../const';

export default {
  // 优惠劵优惠
  couponCalc(dishes, serviceProps) {
    return couponCalculator.calc(dishes, serviceProps);
  },

  // 单商品优惠
  singleProdPrivCalc(dishes, serviceProps, isHDL) {
    return dishes.reduce((acc, cur) => {
      acc += getDishBenefitPrice(cur, serviceProps, dishes, isHDL);
      return acc;
    }, 0);
  },

  // 多商品优惠
  multiProdPrivCalc(dishes, serviceProps) {
    return serviceProps.activityBenefit.benefitMoney || 0;
  },

  privCalc(dishes, serviceProps, isHDL) {
    const { isChecked: isMultiPriv } = serviceProps.wholeOrderBenefit || {};
    return [
      isMultiPriv ? this.multiProdPrivCalc(dishes, serviceProps) : this.singleProdPrivCalc(dishes, serviceProps, isHDL),
      this.couponCalc(dishes, serviceProps),
    ].reduce((acc, cur) => acc + cur, 0);
  },

  // 自动进位计算
  autoCarryCalc(carryRuleVO, charge) {
    const { change: smallChange, price: priceWithClearSmallChange } = carryPrice(carryRuleVO, charge);

    return {
      smallChange,
      priceWithClearSmallChange,
    };
  },

  // 会员积分
  memberPointCalc(serviceProps, realCharge) {
    const {
      integralsInfo,
      couponsProps: { privateRoom },
      integralsDetail,
    } = serviceProps;
    if (!integralsInfo || !integralsInfo.isChecked) {
      return { commutation: 0, integralInUsed: 0 };
    }

    if (!privateRoom) {
      return countIntegralsToCash(realCharge, integralsDetail);
    }

    const { maxDiscount, maxIntegralCanBeUsed } = getMaxIntegralCanBeUsedForYaZuo(realCharge, integralsDetail);

    return { commutation: maxDiscount, integralInUsed: maxIntegralCanBeUsed };
  },

  // 会员积分
  fakeMemberPointCalc(dishes, serviceProps) {
    const {
      couponsProps: { privateRoom },
      integralsDetail,
    } = serviceProps;
    if (!privateRoom) {
      return countIntegralsToCash(this.calcTotalCharge(dishes, serviceProps), integralsDetail);
    }

    const { maxDiscount, maxIntegralCanBeUsed } = getMaxIntegralCanBeUsedForYaZuo(this.calcTotalCharge(dishes, serviceProps), integralsDetail);

    return { commutation: maxDiscount, integralInUsed: maxIntegralCanBeUsed };
  },

  // 配送费用减免
  shippingFeeCalc(dishes, serviceProps) {
    const { freeDeliveryPrice, deliveryPrice } = serviceProps.deliveryProps || {};
    const dishCharge = getDishesPrice(dishes);

    if (dishCharge >= freeDeliveryPrice && deliveryPrice > 0) {
      return deliveryPrice;
    }

    return 0;
  },

  // 礼品卡优惠
  giftCardCalc(serviceProps, realCharge) {
    const { inUseValue = 0 } = serviceProps.cardsProps || {};

    return Math.min(inUseValue, realCharge);
  },

  // 原价
  calcTotalCharge(dishes, serviceProps) {
    const dishCharge = getDishesPrice(dishes);

    return countTotalPriceWithoutBenefit(dishCharge, serviceProps.deliveryProps, serviceProps);
  },

  // 最低消费
  calcLowestConsumptionCharge({ serviceProps, ret, purchaseRealTotal }) {
    const { lowestConsumption } = serviceProps;
    const { total, totalPriv } = ret;
    const chargeValue = total - totalPriv + Math.max(purchaseRealTotal, 0);

    if (!lowestConsumption) {
      return 0;
    }

    if (total - totalPriv + Math.max(purchaseRealTotal, 0) < lowestConsumption) {
      return lowestConsumption - chargeValue;
    }

    return 0;
  },

  // todo remove duplicate calc
  calc(orderedDishesProps, serviceProps, commercialProps = {}, peopleCount, isHDL) {
    const { hasPriviledge } = commercialProps;
    if (hasPriviledge) {
      return this.calcInAddDishMode(serviceProps, commercialProps);
    }

    return this.calcInNormalMode(orderedDishesProps, serviceProps, commercialProps, peopleCount, isHDL);
  },

  // 普通模式
  calcInNormalMode(orderedDishesProps, serviceProps, commercialProps, peopleCount, isHDL) {
    const { dishes } = orderedDishesProps;
    const {
      integralsInfo,
      markUpBenefitProps,
      benefitProps,
      taxInfo: {
        addFeeTaxes = [],
        dishConsumeTaxs = [],
        taxMode = -1,
      },
    } = serviceProps;
    // TODO, 计算加价购,在原来 dishes 的基础上再合并上参与加价购的商品
    const { finalDishes, purchaseRealTotal, purchaseMarketTotal, compareBenefit } = getFinalDishesWidthPurchase(markUpBenefitProps);
    const newDishes = dishes.concat(finalDishes || []);

    const ret = {
      priv: 0,
      total: this.calcTotalCharge(dishes, serviceProps),
      memberPoint: { commutation: 0, integralInUsed: 0 },
      totalPriv: 0,
      coupon: this.couponCalc(dishes, serviceProps),
      real: 0,
      giftCardPriv: 0,
      carry: { smallChange: 0, priceWithClearSmallChange: 0 },
      exciseTaxPriv: benefitProps.taxFee || 0,
      serviceCharge: benefitProps.serviceCharge.initAmount,
      lowestConsumptionCharge: 0, // 最低消费差值
      taxInfo: {},
    };
    let taxInfo = {};

    const beforeAndAfterPriv = {
      beforePrivTotal: 0, // 标记优惠前总价
      afterPrivTotal: 0, // 标记优惠后总价
      beforePrivServiceCharge: 0, // 标记优惠前服务费
      afterPrivServiceCharge: 0, // 标记优惠后服务费
      beforePrivExciseTax: 0, // 标记优惠前消费税
      afterPrivExciseTax: 0, // 标记优惠后消费税
    };

    const { carryRuleVO } = commercialProps;
    const { serviceCharge } = benefitProps;

    // 单、多商品优和优惠劵优惠
    ret.priv = this.privCalc(dishes, serviceProps, isHDL);
    ret.totalPriv += ret.priv;
    beforeAndAfterPriv.beforePrivTotal = ret.total;

    // 配送费减免
    const shippingFee = this.shippingFeeCalc(newDishes, serviceProps);
    if (shippingFee) {
      ret.totalPriv += shippingFee;
    }

    // 积分
    if (integralsInfo && integralsInfo.isChecked) {
      ret.memberPoint = this.memberPointCalc(serviceProps, ret.total - ret.totalPriv);
      ret.totalPriv += ret.memberPoint.commutation;
    }

    // 礼品卡
    const giftCardPriv = this.giftCardCalc(serviceProps, ret.total - ret.totalPriv);
    ret.giftCardPriv = giftCardPriv;
    ret.totalPriv += giftCardPriv;

    beforeAndAfterPriv.afterPrivTotal = ret.total - ret.totalPriv;

    // 服务费计算
    Object.assign(beforeAndAfterPriv, calcServiceCharge({ serviceCharge, beforeAndAfterPriv, peopleCount }));
    ret.serviceCharge = formatPrice(beforeAndAfterPriv.beforePrivServiceCharge + beforeAndAfterPriv.afterPrivServiceCharge);

    // 服务费加入总价
    ret.total += ret.serviceCharge + getOrderExtraPrice(serviceProps);

    // 最低消费
    ret.lowestConsumptionCharge = this.calcLowestConsumptionCharge({ serviceProps, ret, purchaseRealTotal });
    ret.total += ret.lowestConsumptionCharge;

    // 计算菜品的消费税（优惠后的价格）
    if (dishConsumeTaxs.length) {
      // 优惠后的金额(还未计算优惠券分摊)
      const { realPrices } = couponCalculator.getRealPrices(dishes, serviceProps);
      const afterCouponPrices = couponCalculator.getAfterCouponPrices(dishes, serviceProps);

      const dishPrices = Object.keys(realPrices).reduce((res, dishId) => {
        const afterCouponPrice = afterCouponPrices[dishId];
        if (afterCouponPrice && afterCouponPrice.length) {
          res[dishId] = Math.max(realPrices[dishId] - afterCouponPrice.reduce((t, c) => t + c, 0), 0);
        } else {
          res[dishId] = realPrices[dishId];
        }

        return res;
      }, {});

      dishConsumeTaxs.forEach(({ dishId, taxs = [] }) => {
        const dishPrice = dishPrices[dishId];
        if (dishPrice && taxs.length) {
          taxInfo = calcDishForTaxType(dishPrice, taxs, taxInfo, taxMode);
        }
      });
    }

    // 计算附加费的消费税
    if (addFeeTaxes.length) {
      const addFees = [
        ...(benefitProps.extraPrivilege || []),
        {
          ...serviceCharge,
          privilegeAmount: ret.serviceCharge,
        },
      ];

      addFeeTaxes.forEach(({ addFeeId, taxs = [] }) => {
        const addFee = addFees.find(item => item.id === addFeeId);
        if (addFee && addFee.privilegeAmount) {
          taxInfo = calcDishForTaxType(addFee.privilegeAmount, taxs, taxInfo, taxMode);
        }
      });
    }

    // 消费税总额
    ret.taxInfo = Object.keys(taxInfo).map(key => taxInfo[key]);
    ret.exciseTaxPriv = ret.taxInfo.reduce((t, c) => t + c.amount, 0);
    ret.total += taxMode === TAX_MODE.INCLUDE ? 0 : ret.exciseTaxPriv;

    // 进位规则
    if (carryRuleVO) {
      ret.carry = this.autoCarryCalc(carryRuleVO, ret.total - ret.totalPriv);
      ret.totalPriv += ret.carry.smallChange;
    }

    ret.serviceCharge = formatPrice(ret.serviceCharge);
    ret.priv = formatPrice(ret.priv);
    ret.purchasePriv = formatPrice(purchaseMarketTotal - purchaseRealTotal);
    ret.exciseTaxPriv = formatPrice(ret.exciseTaxPriv);
    ret.real = formatPrice(ret.total - ret.totalPriv + Math.max(purchaseRealTotal, 0));
    ret.totalPriv = formatPrice(ret.totalPriv);
    ret.compareBenefit = formatPrice(compareBenefit);
    ret.total = formatPrice(ret.total + purchaseMarketTotal);
    ret.lowestConsumptionCharge = formatPrice(ret.lowestConsumptionCharge);

    return ret;
  },

  // 加菜模式
  calcInAddDishMode(serviceProps, commercialProps) {
    const ret = {
      priv: 0,
      total: 0,
      memberPoint: { commutation: 0, integralInUsed: 0 },
      totalPriv: 0,
      coupon: 0,
      real: 0,
      giftCardPriv: 0,
      purchasePriv: 0,
      carry: { smallChange: 0, priceWithClearSmallChange: 0 },
      exciseTaxPriv: serviceProps.benefitProps.taxFee || 0,
      serviceCharge: formatPrice(serviceProps.benefitProps.serviceCharge.initAmount || 0),
      lowestConsumptionCharge: formatPrice(0),
    };
    const { priviledgeAmount, totalAmount } = serviceProps.benefitProps;
    const { carryRuleVO } = commercialProps;

    ret.total = priviledgeAmount + totalAmount;
    ret.totalPriv = priviledgeAmount;

    if (carryRuleVO) {
      ret.carry = this.autoCarryCalc(carryRuleVO, totalAmount);
      ret.totalPriv += ret.carry.smallChange;
    }

    ret.real = formatPrice(ret.total - ret.totalPriv);
    return ret;
  },
};



// WEBPACK FOOTER //
// ./src/helper/privilege/index.js